import { useEffect, useState, createContext, useContext } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import { contentApi } from '../../services/api';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../common';

// ---------------------------------------------------------------------------
// Language context — toggle ES/EN cambia todos los BiInput/BiTextarea
// ---------------------------------------------------------------------------

type Lang = 'es' | 'en';
const LangContext = createContext<Lang>('es');

// ---------------------------------------------------------------------------
// Types (mirror backend EcommerceSiteConfig — bilingual ES/EN)
// ---------------------------------------------------------------------------

interface BiText { es: string; en: string }

interface HeroSlide {
  id: string;
  image: string;
  title: BiText;
  subtitle: BiText;
  description: BiText;
  cta_label: BiText;
  cta_link: string;
}

interface FeaturedCategory {
  id: string;
  name: BiText;
  slug: string;
  image: string;
}

interface FeaturedProduct {
  product_id: string;
  display_order: number;
}

interface PromoBanner {
  active: boolean;
  text: BiText;
  link: string;
}

interface FooterLink { label: BiText; href: string }
interface FooterColumn { heading: BiText; links: FooterLink[] }

interface PageContent {
  slug: string;
  title: BiText;
  content: BiText;
  active: boolean;
}

interface TrustBadge {
  id: string;
  icon: string;
  label: BiText;
  description: BiText;
}

interface PriceRange {
  id: string;
  label: BiText;
  min?: number;
  max?: number;
}

interface EcommerceSiteConfig {
  hero_slides: HeroSlide[];
  featured_categories: FeaturedCategory[];
  featured_products: FeaturedProduct[];
  trust_badges: TrustBadge[];
  /** Mostrar el bloque de sellos de confianza en la tienda. Ausente/true = visible. */
  show_trust_badges?: boolean;
  /** Modo escaparate: 'deferred' = sin carrito ni pago. Ausente = venta directa. */
  sale_mode?: 'direct' | 'deferred';
  /** Enlace general del botón en modo escaparate (si el producto no tiene el suyo). */
  deferred_url?: string;
  /** Texto del botón en modo escaparate. Vacío = «Saber más». */
  deferred_button_label?: BiText;
  price_ranges: PriceRange[];
  homepage_sections: {
    featured_heading: BiText;
    featured_description: BiText;
    categories_heading: BiText;
    categories_description: BiText;
    cta_heading: BiText;
    cta_description: BiText;
    cta_button: BiText;
  };
  promo_banner: PromoBanner;
  footer_links: { columns: FooterColumn[] };
  page_meta: {
    title: BiText;
    title_template: BiText;
    description: BiText;
  };
  ui_strings: {
    search_placeholder: BiText;
    add_to_cart: BiText;
    out_of_stock: BiText;
    cart_empty: BiText;
    checkout_button: BiText;
    continue_shopping: BiText;
    nav_home: BiText;
    nav_shop: BiText;
    nav_cart: BiText;
    footer_description: BiText;
  };
  pages: PageContent[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const uid = () => `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const emptyBi = (): BiText => ({ es: '', en: '' });

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

// ---------------------------------------------------------------------------
// Bilingual input components
// ---------------------------------------------------------------------------

function BiInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: BiText;
  onChange: (v: BiText) => void;
  placeholder?: { es?: string; en?: string };
}) {
  const lang = useContext(LangContext);
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <input
        type="text"
        value={value[lang] || ''}
        onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
        className={inputClass}
        placeholder={placeholder?.[lang] || ''}
      />
    </div>
  );
}

function BiTextarea({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  label: string;
  value: BiText;
  onChange: (v: BiText) => void;
  rows?: number;
  placeholder?: { es?: string; en?: string };
}) {
  const lang = useContext(LangContext);
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <textarea
        value={value[lang] || ''}
        onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
        rows={rows}
        className={inputClass}
        placeholder={placeholder?.[lang] || ''}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Accordion section wrapper
// ---------------------------------------------------------------------------

function Section({ title, subtitle, defaultOpen, children }: {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {open ? <ChevronUpIcon className="w-5 h-5 text-gray-400" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" />}
      </button>
      {open && <div className="px-6 pb-6 border-t border-gray-100 pt-4">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-editors
// ---------------------------------------------------------------------------

function HeroSlidesEditor({ slides, onChange }: { slides: HeroSlide[]; onChange: (s: HeroSlide[]) => void }) {
  const update = (idx: number, partial: Partial<HeroSlide>) => {
    const next = [...slides];
    next[idx] = { ...next[idx], ...partial };
    onChange(next);
  };
  const remove = (idx: number) => onChange(slides.filter((_, i) => i !== idx));
  const add = () => onChange([...slides, {
    id: uid(),
    image: '',
    title: emptyBi(),
    subtitle: emptyBi(),
    description: emptyBi(),
    cta_label: emptyBi(),
    cta_link: '/productos',
  }]);
  const move = (idx: number, dir: -1 | 1) => {
    const next = [...slides];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {slides.map((slide, idx) => (
        <div key={slide.id} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50/50">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Slide {idx + 1}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <ArrowsUpDownIcon className="w-4 h-4 rotate-180" />
              </button>
              <button onClick={() => move(idx, 1)} disabled={idx === slides.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <ArrowsUpDownIcon className="w-4 h-4" />
              </button>
              <button onClick={() => remove(idx)} className="p-1 text-red-400 hover:text-red-600">
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
          <BiInput label="Subtitulo / Subtitle" value={slide.subtitle} onChange={(v) => update(idx, { subtitle: v })} />
          <BiInput label="Titulo / Title" value={slide.title} onChange={(v) => update(idx, { title: v })} />
          <BiTextarea label="Descripcion / Description" value={slide.description} onChange={(v) => update(idx, { description: v })} rows={2} />
          <div className="grid grid-cols-2 gap-3">
            <BiInput label="Texto boton / CTA Label" value={slide.cta_label} onChange={(v) => update(idx, { cta_label: v })} />
            <div>
              <label className={labelClass}>Enlace boton / CTA Link</label>
              <input type="text" value={slide.cta_link} onChange={(e) => update(idx, { cta_link: e.target.value })} className={inputClass} placeholder="/productos" />
            </div>
          </div>
          <div>
            <label className={labelClass}>URL Imagen / Image URL</label>
            <div className="flex items-center gap-2">
              <input type="text" value={slide.image} onChange={(e) => update(idx, { image: e.target.value })} className={`flex-1 ${inputClass}`} placeholder="https://..." />
              {slide.image && (
                <div className="w-16 h-10 rounded overflow-hidden bg-gray-200 flex-shrink-0">
                  <img src={slide.image} alt="" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir slide
      </button>
    </div>
  );
}

function FeaturedCategoriesEditor({ items, onChange }: { items: FeaturedCategory[]; onChange: (v: FeaturedCategory[]) => void }) {
  const update = (idx: number, partial: Partial<FeaturedCategory>) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...partial };
    onChange(next);
  };
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { id: uid(), name: emptyBi(), slug: '', image: '' }]);

  return (
    <div className="space-y-3">
      {items.map((cat, idx) => (
        <div key={cat.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Categoria {idx + 1}</span>
            <button onClick={() => remove(idx)} className="p-1 text-red-400 hover:text-red-600">
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
          <BiInput label="Nombre / Name" value={cat.name} onChange={(v) => update(idx, { name: v })} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Slug</label>
              <input type="text" value={cat.slug} onChange={(e) => update(idx, { slug: e.target.value })} className={inputClass} placeholder="ropa-mujer" />
            </div>
            <div>
              <label className={labelClass}>URL Imagen</label>
              <div className="flex items-center gap-2">
                <input type="text" value={cat.image} onChange={(e) => update(idx, { image: e.target.value })} className={`flex-1 ${inputClass}`} placeholder="https://..." />
                {cat.image && (
                  <div className="w-10 h-7 rounded overflow-hidden bg-gray-200 flex-shrink-0">
                    <img src={cat.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir categoria
      </button>
    </div>
  );
}

function FeaturedProductsEditor({ items, onChange }: { items: FeaturedProduct[]; onChange: (v: FeaturedProduct[]) => void }) {
  const update = (idx: number, partial: Partial<FeaturedProduct>) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...partial };
    onChange(next);
  };
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { product_id: '', display_order: items.length }]);

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">Introduce el ID del producto (ObjectId de Mongo) que quieres destacar en la home. El orden controla la posicion.</p>
      {items.map((p, idx) => (
        <div key={`${p.product_id}-${idx}`} className="flex items-center gap-2 border border-gray-200 rounded-lg p-2 bg-gray-50/50">
          <span className="text-xs font-semibold text-gray-500 w-8 text-center">{idx + 1}</span>
          <input type="text" value={p.product_id} onChange={(e) => update(idx, { product_id: e.target.value })} className={`flex-1 ${inputClass} font-mono text-xs`} placeholder="ID del producto..." />
          <input type="number" value={p.display_order} onChange={(e) => update(idx, { display_order: parseInt(e.target.value) || 0 })} className={`w-20 ${inputClass}`} />
          <button onClick={() => remove(idx)} className="p-1.5 text-red-400 hover:text-red-600 flex-shrink-0">
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir producto
      </button>
    </div>
  );
}

// Curated set of icons the storefront knows how to render for trust badges.
// Keep in sync with BADGE_ICONS in the storefront (corestore/src/app/page.tsx).
const BADGE_ICONS = [
  'Shield', 'Lock', 'CreditCard', 'Clock', 'Star', 'Heart',
  'Gift', 'Leaf', 'Award', 'Truck', 'Headphones', 'Package',
  'Sparkles', 'MapPin',
] as const;

function TrustBadgesEditor({ items, onChange }: { items: TrustBadge[]; onChange: (v: TrustBadge[]) => void }) {
  const update = (idx: number, partial: Partial<TrustBadge>) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...partial };
    onChange(next);
  };
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  // Máximo 6 sellos: la barra los pinta en una rejilla de 4 y el banner usa 4;
  // más de 6 se ve saturado.
  const add = () => { if (items.length < 6) onChange([...items, { id: uid(), icon: 'Shield', label: emptyBi(), description: emptyBi() }]); };

  return (
    <div className="space-y-3">
      {items.map((badge, idx) => (
        <div key={badge.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sello {idx + 1}</span>
            <button onClick={() => remove(idx)} className="p-1 text-red-400 hover:text-red-600">
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
          <div>
            <label className={labelClass}>Icono</label>
            <select value={badge.icon} onChange={(e) => update(idx, { icon: e.target.value })} className={inputClass}>
              {BADGE_ICONS.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <BiInput label="Titulo / Label" value={badge.label} onChange={(v) => update(idx, { label: v })} />
          <BiInput label="Descripcion / Description" value={badge.description} onChange={(v) => update(idx, { description: v })} />
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir sello {items.length >= 6 && '(maximo 6)'}
      </button>
    </div>
  );
}

function PriceRangesEditor({ items, onChange }: { items: PriceRange[]; onChange: (v: PriceRange[]) => void }) {
  const update = (idx: number, partial: Partial<PriceRange>) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...partial };
    onChange(next);
  };
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { id: uid(), label: emptyBi() }]);
  // Blank number input clears the bound (undefined), any value sets it.
  const parseBound = (raw: string): number | undefined => (raw === '' ? undefined : parseFloat(raw) || 0);

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Deja vacio el minimo o el maximo para dejar ese extremo abierto (p. ej. solo maximo = &quot;menos de X&quot;).</p>
      {items.map((range, idx) => (
        <div key={range.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Rango {idx + 1}</span>
            <button onClick={() => remove(idx)} className="p-1 text-red-400 hover:text-red-600">
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
          <BiInput label="Etiqueta / Label" value={range.label} onChange={(v) => update(idx, { label: v })} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Minimo</label>
              <input type="number" value={range.min ?? ''} onChange={(e) => update(idx, { min: parseBound(e.target.value) })} className={inputClass} placeholder="sin minimo" />
            </div>
            <div>
              <label className={labelClass}>Maximo</label>
              <input type="number" value={range.max ?? ''} onChange={(e) => update(idx, { max: parseBound(e.target.value) })} className={inputClass} placeholder="sin maximo" />
            </div>
          </div>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir rango
      </button>
    </div>
  );
}

function FooterLinksEditor({ columns, onChange }: { columns: FooterColumn[]; onChange: (v: FooterColumn[]) => void }) {
  const updateHeading = (colIdx: number, value: BiText) => {
    const next = [...columns];
    next[colIdx] = { ...next[colIdx], heading: value };
    onChange(next);
  };
  const updateLinkLabel = (colIdx: number, linkIdx: number, value: BiText) => {
    const next = [...columns];
    const links = [...next[colIdx].links];
    links[linkIdx] = { ...links[linkIdx], label: value };
    next[colIdx] = { ...next[colIdx], links };
    onChange(next);
  };
  const updateLinkHref = (colIdx: number, linkIdx: number, value: string) => {
    const next = [...columns];
    const links = [...next[colIdx].links];
    links[linkIdx] = { ...links[linkIdx], href: value };
    next[colIdx] = { ...next[colIdx], links };
    onChange(next);
  };
  const removeLink = (colIdx: number, linkIdx: number) => {
    const next = [...columns];
    next[colIdx] = { ...next[colIdx], links: next[colIdx].links.filter((_, i) => i !== linkIdx) };
    onChange(next);
  };
  const addLink = (colIdx: number) => {
    const next = [...columns];
    next[colIdx] = { ...next[colIdx], links: [...next[colIdx].links, { label: emptyBi(), href: '/' }] };
    onChange(next);
  };
  const removeColumn = (colIdx: number) => onChange(columns.filter((_, i) => i !== colIdx));
  const addColumn = () => onChange([...columns, { heading: emptyBi(), links: [] }]);

  return (
    <div className="space-y-4">
      {columns.map((col, colIdx) => (
        <div key={colIdx} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <BiInput label="Titulo columna / Column heading" value={col.heading} onChange={(v) => updateHeading(colIdx, v)} />
            </div>
            <button onClick={() => removeColumn(colIdx)} className="p-1 mt-6 text-red-400 hover:text-red-600 flex-shrink-0">
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
          {col.links.map((link, linkIdx) => (
            <div key={linkIdx} className="border border-gray-100 rounded-md p-3 bg-white space-y-2">
              <BiInput label="Etiqueta / Label" value={link.label} onChange={(v) => updateLinkLabel(colIdx, linkIdx, v)} />
              <div className="flex items-center gap-2">
                <input type="text" value={link.href} onChange={(e) => updateLinkHref(colIdx, linkIdx, e.target.value)} className={`flex-1 ${inputClass}`} placeholder="/ruta" />
                <button onClick={() => removeLink(colIdx, linkIdx)} className="p-1 text-red-400 hover:text-red-600 flex-shrink-0">
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          <button onClick={() => addLink(colIdx)} className="text-xs text-vida-600 hover:text-vida-700 font-medium">
            + Anadir enlace
          </button>
        </div>
      ))}
      {columns.length < 4 && (
        <button onClick={addColumn} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
          <PlusIcon className="w-4 h-4" /> Anadir columna
        </button>
      )}
    </div>
  );
}

function PagesEditor({ pages, onChange }: { pages: PageContent[]; onChange: (v: PageContent[]) => void }) {
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);

  const update = (idx: number, partial: Partial<PageContent>) => {
    const next = [...pages];
    next[idx] = { ...next[idx], ...partial };
    onChange(next);
  };
  const remove = (idx: number) => onChange(pages.filter((_, i) => i !== idx));
  const add = () => onChange([...pages, { slug: '', title: emptyBi(), content: emptyBi(), active: true }]);

  return (
    <div className="space-y-3">
      {pages.map((page, idx) => {
        const key = page.slug || `new-${idx}`;
        const isExpanded = expandedSlug === key;
        return (
          <div key={key} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50/50">
            <div
              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setExpandedSlug(isExpanded ? null : key)}
            >
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${page.active ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm font-medium text-gray-900">{page.title.es || page.title.en || 'Sin titulo'}</span>
                <span className="text-xs text-gray-400">/{page.slug}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); update(idx, { active: !page.active }); }}
                  className={`px-2 py-0.5 text-xs rounded-full font-medium ${page.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                >
                  {page.active ? 'Activa' : 'Inactiva'}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(idx); }}
                  className="p-1 text-red-400 hover:text-red-600"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-gray-200 pt-3 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <BiInput label="Titulo / Title" value={page.title} onChange={(v) => update(idx, { title: v })} />
                  <div>
                    <label className={labelClass}>Slug (URL)</label>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-400 mr-1">/</span>
                      <input type="text" value={page.slug} onChange={(e) => update(idx, { slug: e.target.value })} className={inputClass} placeholder="sobre-nosotros" />
                    </div>
                  </div>
                </div>
                <BiTextarea
                  label="Contenido / Content (soporta Markdown basico)"
                  value={page.content}
                  onChange={(v) => update(idx, { content: v })}
                  rows={10}
                />
              </div>
            )}
          </div>
        );
      })}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir pagina
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main editor component
// ---------------------------------------------------------------------------

export default function EcommerceSiteEditor() {
  const [config, setConfig] = useState<EcommerceSiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lang, setLang] = useState<Lang>('es');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await contentApi.getEcommerceSiteConfig();
      if (res.data?.data) setConfig(res.data.data);
    } catch (e) {
      console.error('Failed to load ecommerce site config', e);
      toast.error('Error al cargar la configuracion del ecommerce');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await contentApi.saveEcommerceSiteConfig(config);
      setDirty(false);
      toast.success('Configuracion guardada correctamente');
    } catch (e) {
      console.error('Failed to save ecommerce site config', e);
      toast.error('Error al guardar la configuracion');
    } finally {
      setSaving(false);
    }
  };

  const patch = <K extends keyof EcommerceSiteConfig>(key: K, value: EcommerceSiteConfig[K]) => {
    setConfig((prev) => prev ? { ...prev, [key]: value } : prev);
    setDirty(true);
  };

  const patchSection = (field: keyof EcommerceSiteConfig['homepage_sections'], value: BiText) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, homepage_sections: { ...prev.homepage_sections, [field]: value } };
    });
    setDirty(true);
  };

  const patchMeta = (field: keyof EcommerceSiteConfig['page_meta'], value: BiText) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, page_meta: { ...prev.page_meta, [field]: value } };
    });
    setDirty(true);
  };

  const patchUi = (field: keyof EcommerceSiteConfig['ui_strings'], value: BiText) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, ui_strings: { ...prev.ui_strings, [field]: value } };
    });
    setDirty(true);
  };

  const patchPromo = (partial: Partial<PromoBanner>) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, promo_banner: { ...prev.promo_banner, ...partial } };
    });
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-8 text-center text-gray-500">
        No se pudo cargar la configuracion del ecommerce.
      </div>
    );
  }

  return (
    <LangContext.Provider value={lang}>
    <div className="space-y-4">
      {/* Save bar */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-6 py-3 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Ecommerce Web</h2>
          <p className="text-sm text-gray-500">Edita los textos, imagenes, paginas y secciones de la tienda online</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <div className="inline-flex bg-gray-100 rounded-lg p-1" role="tablist" aria-label="Idioma de edicion">
            <button
              type="button"
              onClick={() => setLang('es')}
              aria-pressed={lang === 'es'}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                lang === 'es' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              ES
            </button>
            <button
              type="button"
              onClick={() => setLang('en')}
              aria-pressed={lang === 'en'}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                lang === 'en' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              EN
            </button>
          </div>
          {dirty && <span className="text-xs text-amber-600 font-medium">Cambios sin guardar</span>}
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving && <LoadingSpinner size="sm" />}
            Guardar
          </button>
        </div>
      </div>

      <Section title="Slider Inicial" subtitle="Slides del carrusel principal de la home" defaultOpen>
        <HeroSlidesEditor slides={config.hero_slides} onChange={(s) => patch('hero_slides', s)} />
      </Section>

      <Section title="Categorias Destacadas" subtitle="Categorias que aparecen en la home con imagen">
        <FeaturedCategoriesEditor items={config.featured_categories} onChange={(v) => patch('featured_categories', v)} />
      </Section>

      <Section title="Productos Destacados" subtitle="IDs de productos a destacar en la home (en orden)">
        <FeaturedProductsEditor items={config.featured_products} onChange={(v) => patch('featured_products', v)} />
      </Section>

      <Section title="Modo de venta" subtitle="Venta directa con carrito, o escaparate: sin venta, con un botón que lleva al proveedor">
        <label className="flex items-center gap-2 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={config.sale_mode === 'deferred'}
            onChange={(e) => patch('sale_mode', e.target.checked ? 'deferred' : 'direct')}
            className="rounded border-gray-300 text-vida-600 h-4 w-4"
          />
          <span className="text-sm text-gray-700">Modo escaparate (sin carrito ni pago)</span>
        </label>
        {config.sale_mode === 'deferred' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Enlace general del botón</label>
              <input
                type="url"
                value={config.deferred_url || ''}
                onChange={(e) => patch('deferred_url', e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Se usa en los productos que no tienen su propio enlace de contacto (se pone en la ficha del producto).
              </p>
            </div>
            <BiInput
              label="Texto del botón / Button label"
              value={config.deferred_button_label || emptyBi()}
              onChange={(v) => patch('deferred_button_label', v)}
            />
            <p className="text-xs text-gray-500">Vacío = «Saber más».</p>
          </div>
        )}
      </Section>

      <Section title="Sellos de Confianza" subtitle="Iconos con texto (garantia, pago seguro, etc.) del banner y la barra inferior">
        <label className="flex items-center gap-2 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={config.show_trust_badges !== false}
            onChange={(e) => patch('show_trust_badges', e.target.checked)}
            className="rounded border-gray-300 text-vida-600 h-4 w-4"
          />
          <span className="text-sm text-gray-700">Mostrar este bloque en la tienda</span>
        </label>
        <TrustBadgesEditor items={config.trust_badges || []} onChange={(v) => patch('trust_badges', v)} />
      </Section>

      <Section title="Rangos de Precio" subtitle="Filtros rapidos de precio del lateral del catalogo">
        <PriceRangesEditor items={config.price_ranges || []} onChange={(v) => patch('price_ranges', v)} />
      </Section>

      <Section title="Secciones de Home" subtitle="Encabezados y textos descriptivos de cada seccion">
        <div className="space-y-4">
          <BiInput label="Titulo destacados / Featured heading" value={config.homepage_sections.featured_heading} onChange={(v) => patchSection('featured_heading', v)} />
          <BiTextarea label="Descripcion destacados / Featured description" value={config.homepage_sections.featured_description} onChange={(v) => patchSection('featured_description', v)} rows={2} />
          <BiInput label="Titulo categorias / Categories heading" value={config.homepage_sections.categories_heading} onChange={(v) => patchSection('categories_heading', v)} />
          <BiTextarea label="Descripcion categorias / Categories description" value={config.homepage_sections.categories_description} onChange={(v) => patchSection('categories_description', v)} rows={2} />
          <BiInput label="Titulo CTA / CTA heading" value={config.homepage_sections.cta_heading} onChange={(v) => patchSection('cta_heading', v)} />
          <BiTextarea label="Descripcion CTA / CTA description" value={config.homepage_sections.cta_description} onChange={(v) => patchSection('cta_description', v)} rows={2} />
          <BiInput label="Texto boton CTA / CTA button" value={config.homepage_sections.cta_button} onChange={(v) => patchSection('cta_button', v)} />
        </div>
      </Section>

      <Section title="Banner Promocional" subtitle="Banner opcional que aparece en la parte superior de todas las paginas">
        <div className="space-y-4">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={config.promo_banner.active}
              onChange={(e) => patchPromo({ active: e.target.checked })}
              className="rounded text-vida-600 focus:ring-vida-500"
            />
            <span className="text-sm font-medium text-gray-700">Banner activo</span>
          </label>
          <BiInput label="Texto / Text" value={config.promo_banner.text} onChange={(v) => patchPromo({ text: v })} />
          <div>
            <label className={labelClass}>Enlace / Link</label>
            <input type="text" value={config.promo_banner.link} onChange={(e) => patchPromo({ link: e.target.value })} className={inputClass} placeholder="/productos" />
          </div>
        </div>
      </Section>

      <Section title="Enlaces del Footer" subtitle="Columnas de enlaces en el pie de pagina">
        <FooterLinksEditor columns={config.footer_links.columns} onChange={(cols) => patch('footer_links', { columns: cols })} />
      </Section>

      <Section title="Paginas del Sitio" subtitle="Contenido de paginas estaticas (Sobre nosotros, FAQ, Envios, Legal, etc.)">
        <PagesEditor pages={config.pages || []} onChange={(p) => patch('pages', p)} />
      </Section>

      <Section title="Metadatos de Pagina" subtitle="Titulo y descripcion para SEO del ecommerce">
        <div className="space-y-4">
          <BiInput label="Titulo por defecto / Default title" value={config.page_meta.title} onChange={(v) => patchMeta('title', v)} />
          <BiInput label="Plantilla titulo / Title template" value={config.page_meta.title_template} onChange={(v) => patchMeta('title_template', v)} placeholder={{ es: '%s | Tienda', en: '%s | Shop' }} />
          <BiTextarea label="Meta descripcion / Meta description" value={config.page_meta.description} onChange={(v) => patchMeta('description', v)} rows={2} />
        </div>
      </Section>

      <Section title="Textos de Interfaz" subtitle="Labels y placeholders fijos del ecommerce">
        <div className="space-y-4">
          <BiInput label="Placeholder busqueda / Search placeholder" value={config.ui_strings.search_placeholder} onChange={(v) => patchUi('search_placeholder', v)} />
          <BiInput label="Anadir al carrito / Add to cart" value={config.ui_strings.add_to_cart} onChange={(v) => patchUi('add_to_cart', v)} />
          <BiInput label="Sin stock / Out of stock" value={config.ui_strings.out_of_stock} onChange={(v) => patchUi('out_of_stock', v)} />
          <BiInput label="Carrito vacio / Cart empty" value={config.ui_strings.cart_empty} onChange={(v) => patchUi('cart_empty', v)} />
          <BiInput label="Boton checkout / Checkout button" value={config.ui_strings.checkout_button} onChange={(v) => patchUi('checkout_button', v)} />
          <BiInput label="Seguir comprando / Continue shopping" value={config.ui_strings.continue_shopping} onChange={(v) => patchUi('continue_shopping', v)} />
          <div className="border-t border-gray-200 pt-4 mt-4">
            <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wider">Navegacion / Navigation</p>
            <div className="space-y-3">
              <BiInput label="Inicio / Home" value={config.ui_strings.nav_home} onChange={(v) => patchUi('nav_home', v)} />
              <BiInput label="Tienda / Shop" value={config.ui_strings.nav_shop} onChange={(v) => patchUi('nav_shop', v)} />
              <BiInput label="Carrito / Cart" value={config.ui_strings.nav_cart} onChange={(v) => patchUi('nav_cart', v)} />
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4 mt-4">
            <BiInput label="Descripcion footer / Footer description" value={config.ui_strings.footer_description} onChange={(v) => patchUi('footer_description', v)} />
          </div>
        </div>
      </Section>

      {/* Bottom save button */}
      {dirty && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving && <LoadingSpinner size="sm" />}
            Guardar cambios
          </button>
        </div>
      )}
    </div>
    </LangContext.Provider>
  );
}
